import { useEffect, useState } from "preact/hooks";
import { api } from "./api-client";
import { transcodeForUpload } from "./image-transcode";
import { useClipboardCopy } from "./use-clipboard-copy";
import type { ImageDto } from "@shared/types";

type BatchProgress = { done: number; total: number };

type Options = {
  projectIdOrSlug: string;
  open: boolean;
};

export type ImageGallery = {
  items: ImageDto[] | null;
  loadError: string | null;
  uploadError: string | null;
  batchProgress: BatchProgress | null;
  deleting: string | null;
  copied: string | null;
  uploading: boolean;
  uploadFiles: (files: FileList | File[]) => Promise<boolean>;
  deleteImage: (item: ImageDto) => Promise<void>;
  copyImage: (item: ImageDto) => Promise<void>;
};

function mergeImageItems(existing: ImageDto[] | null, loaded: ImageDto[]) {
  if (!existing) return loaded;
  const seen = new Set(existing.map((item) => item.r2Key));
  return [
    ...existing,
    ...loaded.filter((item) => {
      if (seen.has(item.r2Key)) return false;
      seen.add(item.r2Key);
      return true;
    }),
  ];
}

export function useImageGallery({
  projectIdOrSlug,
  open,
}: Options): ImageGallery {
  const [items, setItems] = useState<ImageDto[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [batchProgress, setBatchProgress] = useState<BatchProgress | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const { copied, copy } = useClipboardCopy();

  const uploading = batchProgress !== null;

  // Lazy-load when the modal opens. Merging preserves successful uploads that
  // finish while the initial list request is still in flight.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setItems(null);
    setLoadError(null);
    setUploadError(null);
    void (async () => {
      try {
        const res = await api.listImages(projectIdOrSlug);
        if (alive) setItems((prev) => mergeImageItems(prev, res.items));
      } catch (e) {
        if (alive) setLoadError((e as Error).message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [open, projectIdOrSlug]);

  async function uploadFiles(files: FileList | File[]): Promise<boolean> {
    // The file input is disabled during a batch; this also rejects racing DnD
    // drops before they can start a second sequential upload loop.
    if (batchProgress !== null) return false;
    const list = Array.from(files).filter((file) =>
      file.type.startsWith("image/"),
    );
    if (list.length === 0) return false;

    setUploadError(null);
    setBatchProgress({ done: 0, total: list.length });
    const failures: { filename: string; error: string }[] = [];
    // Upload sequentially to keep per-file progress visible without hammering
    // D1 and R2 when a folder of images is dropped.
    for (const file of list) {
      try {
        const normalized = await transcodeForUpload(file);
        const image = await api.uploadImage({
          projectIdOrSlug,
          file: normalized.file,
          width: normalized.width,
          height: normalized.height,
        });
        setItems((prev) => (prev ? [image, ...prev] : [image]));
      } catch (e) {
        failures.push({ filename: file.name, error: (e as Error).message });
      } finally {
        setBatchProgress((prev) =>
          prev ? { done: prev.done + 1, total: prev.total } : prev,
        );
      }
    }

    setBatchProgress(null);
    if (failures.length > 0) {
      // Include each file's reason: "why" differs per file (too large,
      // transcode refused, server error) and a bare name list hides it.
      const details = failures
        .map((failure) => `${failure.filename}（${failure.error}）`)
        .join("、");
      setUploadError(`${failures.length} 件のアップロードに失敗しました: ${details}`);
    }
    return true;
  }

  async function deleteImage(item: ImageDto): Promise<void> {
    setDeleting(item.r2Key);
    try {
      await api.deleteImage(projectIdOrSlug, item.r2Key);
      setItems((prev) =>
        prev ? prev.filter((image) => image.r2Key !== item.r2Key) : prev,
      );
    } catch (e) {
      setUploadError(`削除失敗: ${(e as Error).message}`);
    } finally {
      setDeleting(null);
    }
  }

  async function copyImage(item: ImageDto): Promise<void> {
    try {
      await copy(item.r2Key, item.url);
    } catch {
      setUploadError("クリップボードへのコピーに失敗しました");
    }
  }

  return {
    items,
    loadError,
    uploadError,
    batchProgress,
    deleting,
    copied,
    uploading,
    uploadFiles,
    deleteImage,
    copyImage,
  };
}
